import type { CreatePollData, Poll } from '@features/polls/types/poll.types';
import { MediaType } from '@shared/types/media.types';
import { UserProfile } from '@shared/types/user';
import { Timestamp } from 'firebase/firestore';

export enum PostType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  LINK = 'link',
  POLL = 'poll',
}

export enum PostVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  CONNECTIONS = 'connections',
}

export interface MediaVariant {
  url: string;
  width?: number;
  height?: number;
  quality?: string;
  format: string;
  type: 'original' | 'thumbnail' | 'compressed' | 'web';
}

export interface MediaItem {
  id?: string;
  type: MediaType;
  variants: MediaVariant[];
}

export type {
  CreatePollData,
  CreatePollOptionData,
  CreatePollQuestionData,
  Poll,
  PollOption,
  PollQuestion,
} from '@features/polls/types/poll.types';

export interface Post {
  id?: string;
  author: UserProfile;
  content: string;
  type: PostType;
  visibility: PostVisibility;
  media?: MediaItem[];
  poll?: Poll;
  likes: number;
  shares: number;
  comments: number;
  commentCount: number;
  sharedBy?: UserProfile;
  sharedPost?: Post;
  isLiked?: boolean;
  isShared?: boolean;
  isCommented?: boolean;
  commentList?: Comment[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface PostResponse {
  success: boolean;
  post: Post;
}

export interface Comment {
  id?: string;
  postId: string;
  author: UserProfile;
  content: string;
  likes: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CommentResponse {
  success: boolean;
  comment: Comment;
}

export interface Like {
  id?: string;
  postId: string;
  userId: string;
  createdAt: Timestamp;
}

export interface Share {
  id?: string;
  postId: string;
  userId: string;
  createdAt: Timestamp;
}

export interface PostNotification {
  type: 'post_like' | 'post_comment' | 'post_share';
  postId: string;
  userId: string;
  actorId: string;
  read: boolean;
  createdAt: Date;
}

export interface CreatePostData {
  content: string;
  type: PostType;
  visibility: PostVisibility;
  media?: MediaItem[];
  poll?: CreatePollData;
}

export interface PostsResponse {
  success: boolean;
  posts: Post[];
  total: number;
  currentPage: number;
  hasNextPage: boolean;
}

export interface CommentsResponse {
  success: boolean;
  comments: Comment[];
  total: number;
  currentPage: number;
  hasNextPage: boolean;
}
